import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import Layout from '@shared/components/layout'
import styles from './style.module.scss'
import { Container } from 'react-bootstrap'
import { F1EditorsPick, HotStories } from '@shared/components/F1Components/EditorsPick'
import dynamic from 'next/dynamic'
import queryGraphql from '@shared/components/queryGraphql'
import { handleApiError, parallelCalls } from '@shared-submodule/utils'
import { GET_CATEGORY_NEWS_VIDEOS, GET_FORMULA_NEWS_VIDEOS } from '@graphql/series/home.query'
import { reviveAdData } from '@shared/components/reviveAds/reviveAdData'
import Image from 'next/future/image'
import hofBanner from '@assets/images/hofBanner.svg'
import hofBannerDark from '@assets/images/hofBannerDark.svg'
import hofMwebBannerDark from '@assets/images/hofMwebBannerDark.svg'
import hofMwebBanner from '@assets/images/hofMwebBanner.svg'
import CustomLink from '@shared/components/customLink'
import GlobalEventsContext from '@shared/components/global-events/GlobalEventsContext'
import { GRAPHQL_URL } from '@shared-submodule/sm-constants'

const KnowYourCircuits = dynamic(() => import('@shared/components/F1Components/KnowYourCircuits'))
const KnowYourCars = dynamic(() => import('@shared/components/F1Components/KnowYourCars'))
const KnowYourDriver = dynamic(() => import('@shared/components/F1Components/KnowYourDrivers'))
const NewsForYou = dynamic(() => import('@shared/components/F1Components/NewsForYou'))
const F1Header = dynamic(() => import('@shared/components/F1Components/F1Header'))
const ReviveAds = dynamic(() => import('@shared/components/reviveAds'), { ssr: false })

function Formula1({ seoData, news, driversData, editorsData, raceData }) {
  const editors = news?.listSimpleCategoryArticlesVideosFront?.oArticles?.aResults.slice(0, 3)
  const data = news?.listSimpleCategoryArticlesVideosFront?.oArticles?.aResults.slice(3)
  const editorsList = editorsData?.listSimpleCategoryArticlesVideosFront?.oArticles?.aResults.slice(0, 3)
  const { addTop, mobSm, addBottom, addLeft } = reviveAdData
  const { stateGlobalEvents } = useContext(GlobalEventsContext)
  return (
    <Layout data={{ oSeo: seoData }}>
      <>
        <div className={styles.audioWide}>
          <F1Header raceData={raceData} />
          <NewsForYou editorsList={editorsList} />
          <Container className={styles.gap}>
            <F1EditorsPick editorsData={editors} />
            <div className="w-full d-none d-xl-flex justify-content-center my-3">
              <ReviveAds {...addTop} className={'my-3'} />
            </div>
            <div className="w-full d-flex d-xl-none justify-content-center my-1">
              <ReviveAds {...mobSm} className={'my-1'} />
            </div>
            {driversData?.data?.length > 0 && <KnowYourDriver driversData={driversData} />}
            <KnowYourCars />
            <CustomLink href="/formula1-racing/hall-of-fame">
              <div className={styles.hof_banner}>
                <picture>
                  <source srcSet={stateGlobalEvents?.darkModeEna === 'dark' ? hofBannerDark : hofBanner} media="(min-width: 768px)" />
                  <source
                    srcSet={stateGlobalEvents?.darkModeEna === 'dark' ? hofMwebBannerDark : hofMwebBanner}
                    media="(max-width: 768px)"
                  />
                  <Image src={stateGlobalEvents?.darkModeEna === 'dark' ? hofBannerDark : hofBanner} alt="Hall of Fame" />
                </picture>
              </div>
            </CustomLink>
            {raceData?.data?.length > 0 && <KnowYourCircuits raceData={raceData} />}
            <div className="w-full d-none d-xl-flex justify-content-center my-2">
              <ReviveAds {...addBottom} className={'my-2 text-center'} />
            </div>
            <div className="w-full d-flex d-xl-none justify-content-center my-2">
              <ReviveAds {...addLeft} className={'my-2'} />
            </div>
            <HotStories news={data} />
          </Container>
        </div>
      </>
    </Layout>
  )
}

export async function getServerSideProps({ res, resolvedUrl }) {
  try {
    res.setHeader('Cache-Control', 'public, max-age=600')

    const GET_SEO_DATA = (await import('@graphql/article/article.query')).GET_SEO_DATA
    const { data: seo } = await queryGraphql(GET_SEO_DATA, { input: { sSlug: 'formula1-racing' } })

    const BASE_URL = GRAPHQL_URL.replace(/\/graphql$/, '')

    const urlDrivers = new URL(`${BASE_URL}/api/global-widget/f1/drivers`)
    const urlRaces = new URL(`${BASE_URL}/api/global-widget/f1/races`)
    urlDrivers.searchParams.append('nLimit', '3')
    urlRaces.searchParams.append('nLimit', '24')

    const [driversResponse, raceResponse] = await Promise.all([
      fetch(urlDrivers, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          'Content-Type': 'application/json'
        }
      }),
      fetch(urlRaces, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          'Content-Type': 'application/json'
        }
      })
    ])

    const [driversData, raceData] = await Promise.all([driversResponse.json(), raceResponse.json()])

    const [news, editorsData] = await parallelCalls([
      queryGraphql(GET_CATEGORY_NEWS_VIDEOS, {
        input: {
          nLimit: 18,
          nSkip: 0,
          iCategoryId: '66226bd0c8717f761bf48525',
          eType: 'vn'
        }
      }),
      queryGraphql(GET_FORMULA_NEWS_VIDEOS, {
        input: {
          bEditorsPick: true,
          nLimit: 18,
          nSkip: 0,
          iCategoryId: '66226bd0c8717f761bf48525',
          eType: 'n'
        }
      })
    ])
    return {
      props: {
        seoData: seo?.getSeoData || {},
        news: news || {},
        driversData: driversData || {},
        editorsData: editorsData || {},
        raceData: raceData || {}
      }
    }
  } catch (e) {
    console.error('Error fetching data:', e)
    res.setHeader('Cache-Control', 'no-cache')
    const status = handleApiError(e, resolvedUrl)
    return status
  }
}

Formula1.propTypes = {
  seoData: PropTypes.object,
  news: PropTypes.object,
  driversData: PropTypes.object,
  editorsData: PropTypes.object,
  raceData: PropTypes.object
}

export default Formula1
